'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  normalizeI18nResolvedLanguage,
  persistPreferredLocale,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from '@repo/common/i18n';
import { DataTestIds } from '@repo/frontend-shared';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSClickAway } from '@repo/sds/SDSClickAway';
import { SDSPrimitiveButton } from '@repo/sds/SDSPrimitiveButton';

import { useFeatureFlag } from '../hooks/use-feature-flag';

import './language-switcher.scss';

function languageSwitcherOptionKey(locale: SupportedLocale): `languageSwitcher.options.${SupportedLocale}` {
  return `languageSwitcher.options.${locale}`;
}

function languageSwitcherTriggerAbbrevKey(
  locale: SupportedLocale,
): `languageSwitcher.triggerAbbrev.${SupportedLocale}` {
  return `languageSwitcher.triggerAbbrev.${locale}`;
}

const SWITCHER_LOCALES_ORDERED = [...SUPPORTED_LOCALES];

export function LanguageSwitcher() {
  const isEnabled = useFeatureFlag('language_switcher');
  const { t, i18n } = useTranslation('common');
  const [open, setOpen] = useState(false);

  if (!isEnabled) {
    return null;
  }

  const activeLocale = normalizeI18nResolvedLanguage(i18n.resolvedLanguage);
  const triggerLabel = t(languageSwitcherTriggerAbbrevKey(activeLocale));

  const selectLocale = (locale: SupportedLocale) => {
    persistPreferredLocale(locale);
    void i18n.changeLanguage(locale).catch((err: unknown) => {
      console.error('[LanguageSwitcher] changeLanguage failed', err);
    });
    setOpen(false);
  };

  return (
    <SDSClickAway onClickAway={() => setOpen(false)} className="LanguageSwitcher">
      <SDSButton
        type="button"
        mode="outline"
        size="small"
        text={triggerLabel}
        ariaLabel={t('languageSwitcher.ariaLabel')}
        dataTestId={DataTestIds.COMMON.LANGUAGE_SWITCHER.TRIGGER}
        onClick={() => setOpen(o => !o)}
      />
      {open && (
        <ul className="LanguageSwitcher__dropdown">
          {SWITCHER_LOCALES_ORDERED.map(locale => (
            <li key={locale}>
              <SDSPrimitiveButton
                type="button"
                className={`LanguageSwitcher__option${activeLocale === locale ? ' LanguageSwitcher__option--active' : ''}`}
                dataTestId={`${DataTestIds.COMMON.LANGUAGE_SWITCHER.OPTION_PREFIX}-${locale}`}
                aria-current={activeLocale === locale ? 'true' : undefined}
                onClick={() => selectLocale(locale)}
              >
                {t(languageSwitcherOptionKey(locale))}
              </SDSPrimitiveButton>
            </li>
          ))}
        </ul>
      )}
    </SDSClickAway>
  );
}
